/*
 * ViroxaNest storefront behaviour for Horizon: voice search, mobile drawer, hero carousel,
 * footer accordions and back-to-top. Dependency-free; every feature degrades to plain HTML.
 * Re-initialises on Theme Editor section reloads.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- Voice search ---------- */

const SpeechRecognitionImpl = window.SpeechRecognition || window.webkitSpeechRecognition;

const VOICE_MESSAGES = {
  unsupported: 'Voice search is not supported in this browser. Please type your search instead.',
  listening: 'Listening… say what you are looking for.',
  heard: 'Check the text, then press search.',
  'not-allowed': 'Microphone access was blocked. Allow it in your browser settings, or type your search.',
  'service-not-allowed': 'Voice search is unavailable in this browser. Please type your search instead.',
  'no-speech': 'We did not hear anything. Tap the microphone and try again.',
  'audio-capture': 'No microphone was found. Please type your search instead.',
  network: 'Voice search needs an internet connection. Please type your search instead.',
  'language-not-supported': 'Voice search does not support this language here. Please type your search.',
  generic: 'Voice search did not work. Please try again or type your search.',
};

/** @param {HTMLFormElement} form */
function initVoiceSearch(form) {
  const button = form.querySelector('[data-vx-voice]');
  const input = form.querySelector('[data-vx-search-input]');
  const status = form.querySelector('[data-vx-voice-status]');
  if (!button || !input || !status || button.dataset.vxReady) return;
  button.dataset.vxReady = 'true';

  let recognition = null;
  let listening = false;
  let hideTimer = 0;

  const show = (message, isError = false, autoHide = true) => {
    window.clearTimeout(hideTimer);
    status.textContent = message;
    status.classList.toggle('is-error', isError);
    status.hidden = false;
    if (autoHide) hideTimer = window.setTimeout(() => (status.hidden = true), 6000);
  };

  const setListening = (value) => {
    listening = value;
    button.setAttribute('aria-pressed', String(value));
    button.setAttribute('aria-label', value ? 'Stop voice search' : 'Search by voice');
  };

  button.addEventListener('click', () => {
    if (!SpeechRecognitionImpl) {
      show(VOICE_MESSAGES.unsupported, true);
      input.focus();
      return;
    }

    if (listening && recognition) {
      recognition.stop();
      return;
    }

    // The recogniser is created only after an explicit tap, so the browser asks for the
    // microphone at that moment and never before. One utterance per tap, no continuous listening.
    recognition = new SpeechRecognitionImpl();
    recognition.lang = button.dataset.lang || document.documentElement.lang || navigator.language || 'en-US';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    let finalText = '';

    recognition.onstart = () => {
      setListening(true);
      show(VOICE_MESSAGES.listening, false, false);
    };

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += transcript;
        else interim += transcript;
      }
      input.value = (finalText || interim).trim();
    };

    recognition.onerror = (event) => {
      setListening(false);
      if (event.error === 'aborted') return;
      show(VOICE_MESSAGES[event.error] || VOICE_MESSAGES.generic, true);
    };

    recognition.onend = () => {
      const wasListening = listening;
      setListening(false);
      recognition = null;
      if (!wasListening) return;
      if (input.value.trim()) {
        show(VOICE_MESSAGES.heard);
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      } else if (status.textContent === VOICE_MESSAGES.listening) {
        show(VOICE_MESSAGES['no-speech'], true);
      }
    };

    try {
      recognition.start();
    } catch (error) {
      setListening(false);
      show(VOICE_MESSAGES.generic, true);
    }
  });

  // Never keep the microphone open when the page is hidden or left.
  const stop = () => recognition?.abort();
  document.addEventListener('visibilitychange', () => document.hidden && stop());
  window.addEventListener('pagehide', stop);

  form.addEventListener('submit', (event) => {
    stop();
    if (!input.value.trim()) {
      event.preventDefault();
      input.focus();
    }
  });
}

/* ---------- Mobile drawer ---------- */

/**
 * Slide-in panel built on a native <dialog> (focus trap, Escape and inert background come from the browser).
 * @param {HTMLDialogElement} dialog
 * @param {HTMLElement[]} openButtons
 * @param {{ onOpen?: () => void, closeAtQuery?: string }} [options]
 */
function initDialog(dialog, openButtons, options = {}) {
  if (!(dialog instanceof HTMLDialogElement) || !openButtons.length || dialog.dataset.vxReady) return;
  dialog.dataset.vxReady = 'true';

  /** @type {HTMLElement | null} */
  let opener = null;

  const open = (event) => {
    if (dialog.open) return;
    opener = event.currentTarget;
    dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    openButtons.forEach((button) => button.setAttribute('aria-expanded', 'true'));
    dialog.querySelector('[data-vx-drawer-close]')?.focus();
    options.onOpen?.();
  };

  const close = () => {
    if (!dialog.open || dialog.classList.contains('is-closing')) return;
    if (reducedMotion.matches) {
      dialog.close();
      return;
    }
    dialog.classList.add('is-closing');
    dialog.addEventListener(
      'animationend',
      () => {
        dialog.classList.remove('is-closing');
        dialog.close();
      },
      { once: true }
    );
  };

  openButtons.forEach((button) => button.addEventListener('click', open));
  dialog.querySelectorAll('[data-vx-drawer-close]').forEach((button) => button.addEventListener('click', close));

  // Escape: let our animation run instead of the instant native close.
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });

  // Click on the backdrop (outside the panel) closes the panel.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });

  dialog.addEventListener('close', () => {
    document.documentElement.removeAttribute('scroll-lock');
    openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    const target = opener && opener.offsetParent !== null ? opener : openButtons.find((b) => b.offsetParent !== null);
    target?.focus({ preventScroll: true });
  });

  if (options.closeAtQuery) {
    window.matchMedia(options.closeAtQuery).addEventListener('change', (event) => {
      if (event.matches && dialog.open) dialog.close();
    });
  }
}

/** @param {HTMLElement} header */
function initDrawer(header) {
  const dialog = header.querySelector('[data-vx-drawer]');
  const openButtons = Array.from(header.querySelectorAll('[data-vx-drawer-open]'));
  // Close when switching to the desktop layout where the menu buttons are hidden.
  initDialog(/** @type {HTMLDialogElement} */ (dialog), openButtons, { closeAtQuery: '(min-width: 1024px)' });

  const panel = header.querySelector('[data-vx-wishlist-panel]');
  const app = panel?.querySelector('[data-vx-wishlist-page]');
  const panelButtons = Array.from(header.querySelectorAll('[data-vx-wishlist-open]'));
  panelButtons.forEach((button) => button.setAttribute('aria-controls', panel?.id || ''));
  initDialog(/** @type {HTMLDialogElement} */ (panel), panelButtons, {
    onOpen: () => {
      if (app instanceof HTMLElement) initWishlistPage(app, true);
    },
  });
}

/* ---------- Mobile bottom bar shortcuts ---------- */

/** @param {HTMLElement} header */
function initShortcuts(header) {
  if (header.dataset.vxShortcuts) return;
  header.dataset.vxShortcuts = 'true';
  const input = header.querySelector('[data-vx-search-input]');

  header.querySelector('[data-vx-voice-shortcut]')?.addEventListener('click', () => {
    // Runs inside the tap, so the browser can still show its microphone prompt.
    const mic = header.querySelector('[data-vx-voice]');
    if (mic) mic.click();
    else input?.focus();
  });

  header.querySelector('[data-vx-search-shortcut]')?.addEventListener('click', () => input?.focus());
}

/* ---------- Wishlist (stored only in this browser) ---------- */

const WISHLIST_KEY = 'vx-wishlist';
const WISHLIST_MAX = 60;
const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]{0,254}$/i;

function readWishlist() {
  try {
    const value = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string' && HANDLE_PATTERN.test(handle)) : [];
  } catch (error) {
    return [];
  }
}

function writeWishlist(list) {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list.slice(0, WISHLIST_MAX)));
  } catch (error) {
    /* storage unavailable (private mode): the button still toggles for this page view */
  }
  syncWishlistUI();
}

function syncWishlistUI() {
  const list = readWishlist();
  document.querySelectorAll('[data-vx-wishlist-count]').forEach((badge) => {
    badge.textContent = list.length > 99 ? '99+' : String(list.length);
    badge.hidden = list.length === 0;
  });
  document.querySelectorAll('[data-vx-wishlist-toggle]').forEach((button) => {
    const saved = list.includes(button.dataset.handle);
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute('aria-label', saved ? button.dataset.labelRemove : button.dataset.labelAdd);
    const text = button.querySelector('[data-text-add]');
    if (text) text.textContent = saved ? text.dataset.textRemove : text.dataset.textAdd;
  });
}

document.addEventListener('click', (event) => {
  const button = /** @type {HTMLElement} */ (event.target).closest?.('[data-vx-wishlist-toggle]');
  if (!button) return;
  event.preventDefault();
  const handle = button.dataset.handle;
  if (!handle || !HANDLE_PATTERN.test(handle)) return;
  const list = readWishlist();
  const index = list.indexOf(handle);
  if (index === -1) list.unshift(handle);
  else list.splice(index, 1);
  writeWishlist(list);
});

window.addEventListener('storage', (event) => {
  if (event.key === WISHLIST_KEY) syncWishlistUI();
});

/** Builds an element with text content only (never HTML). */
function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** @param {HTMLElement} root */
async function initWishlistPage(root, force = false) {
  if (root.hasAttribute('data-vx-lazy') && !force) return;
  if (root.dataset.vxReady) {
    if (force) root.vxRender?.();
    return;
  }
  root.dataset.vxReady = 'true';
  const grid = root.querySelector('[data-vx-wishlist-grid]');
  const status = root.querySelector('[data-vx-wishlist-status]');
  if (!grid || !status) return;

  const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR';
  let money;
  try {
    money = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency });
  } catch (error) {
    money = { format: (value) => value.toFixed(2) };
  }
  const formatPrice = (cents) => money.format(cents / 100);

  const render = async () => {
    const handles = readWishlist();
    grid.replaceChildren();
    if (!handles.length) {
      status.textContent = root.dataset.emptyText || 'Your wishlist is empty.';
      return;
    }
    status.textContent = 'Loading your wishlist…';

    const products = await Promise.all(
      handles.map((handle) =>
        fetch(`${shopRoot}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    );

    // Drop products that no longer exist in the store.
    const available = products.filter(Boolean);
    if (available.length !== handles.length) writeWishlist(available.map((product) => product.handle));

    if (!available.length) {
      status.textContent = root.dataset.emptyText || 'Your wishlist is empty.';
      return;
    }
    status.textContent = `${available.length} saved ${available.length === 1 ? 'product' : 'products'}`;

    for (const product of available) {
      const item = el('li', 'vx-wishlist__item');
      const link = el('a', 'vx-wishlist__media');
      link.href = product.url;
      const imageSrc = typeof product.featured_image === 'string' ? product.featured_image : '';
      if (/^(https:)?\/\//.test(imageSrc)) {
        const img = el('img', 'vx-wishlist__img');
        img.src = `${imageSrc}${imageSrc.includes('?') ? '&' : '?'}width=600`;
        img.alt = product.title;
        img.width = 600;
        img.height = 600;
        img.loading = 'lazy';
        link.append(img);
      }
      const body = el('div', 'vx-wishlist__body');
      const title = el('a', 'vx-wishlist__title', product.title);
      title.href = product.url;
      const price = el('p', 'vx-wishlist__price');
      price.append(el('span', '', formatPrice(product.price)));
      if (product.compare_at_price && product.compare_at_price > product.price) {
        price.append(el('s', 'vx-wishlist__compare', formatPrice(product.compare_at_price)));
      }
      body.append(title, price);
      if (!product.available) body.append(el('p', 'vx-wishlist__soldout', 'Sold out'));

      const actions = el('div', 'vx-wishlist__actions');
      const variant = product.variants.find((v) => v.available);
      if (product.available && product.variants.length === 1 && variant) {
        // Native Shopify cart form: works without the AJAX API and keeps checkout untouched.
        const form = el('form', 'vx-wishlist__form');
        form.method = 'post';
        form.action = `${shopRoot}cart/add`;
        const id = el('input');
        id.type = 'hidden';
        id.name = 'id';
        id.value = String(variant.id);
        const add = el('button', 'vx-btn vx-btn--primary', 'Add to cart');
        add.type = 'submit';
        form.append(id, add);
        actions.append(form);
      } else {
        const view = el('a', 'vx-btn vx-btn--outline', product.available ? 'Choose options' : 'View product');
        view.href = product.url;
        actions.append(view);
      }
      const remove = el('button', 'vx-wishlist__remove', 'Remove');
      remove.type = 'button';
      remove.setAttribute('aria-label', `Remove ${product.title} from wishlist`);
      remove.addEventListener('click', () => {
        writeWishlist(readWishlist().filter((handle) => handle !== product.handle));
        render();
      });
      actions.append(remove);
      body.append(actions);
      item.append(link, body);
      grid.append(item);
    }
  };

  root.vxRender = render;
  render();
}

/* ---------- Product page "Back to collection" (same-store destinations only, no history.back()) ---------- */

const COLLECTION_CONTEXT_KEY = 'vx-collection-context';
// Optional locale prefix (/en-in/), then /collections/<handle>.
const COLLECTION_PATH = /^\/(?:[a-z]{2}(?:-[a-z0-9]{2,4})?\/)?collections\/[a-z0-9][a-z0-9-]{0,254}\/?$/i;
const PRODUCT_PATH = /^\/(?:[a-z]{2}(?:-[a-z0-9]{2,4})?\/)?(?:collections\/[a-z0-9-]+\/)?products\/[a-z0-9-]+\/?$/i;
const SEARCH_PATH = /^\/(?:[a-z]{2}(?:-[a-z0-9]{2,4})?\/)?search\/?$/i;

function readCollectionContext() {
  try {
    const value = JSON.parse(sessionStorage.getItem(COLLECTION_CONTEXT_KEY) || 'null');
    if (value && typeof value.path === 'string' && COLLECTION_PATH.test(value.path) && typeof value.title === 'string') {
      return { path: value.path, search: typeof value.search === 'string' ? value.search : '', title: value.title };
    }
  } catch (error) {
    /* storage unavailable */
  }
  return null;
}

function writeCollectionContext(path, search, title) {
  if (!COLLECTION_PATH.test(path) || !title) return;
  try {
    sessionStorage.setItem(
      COLLECTION_CONTEXT_KEY,
      JSON.stringify({ path, search: search.slice(0, 500), title: title.slice(0, 120) })
    );
  } catch (error) {
    /* storage unavailable: the server-rendered fallback is used */
  }
}

/** Remember the collection page being viewed so product pages can link back to it. */
function rememberCollection(header) {
  const title = header.dataset.vxCollectionTitle;
  if (!header.dataset.vxCollectionUrl || !title) return;
  writeCollectionContext(location.pathname, location.search, title);
}

/** Same-origin referrer, or null when there is none, it is external, or it cannot be read. */
function sameStoreReferrer() {
  try {
    if (!document.referrer) return null;
    const url = new URL(document.referrer);
    return url.origin === location.origin ? url : null;
  } catch (error) {
    return null;
  }
}

/** @param {HTMLAnchorElement} link */
function initBackButton(link) {
  if (link.dataset.vxReady) return;
  link.dataset.vxReady = 'true';
  const titleNode = link.querySelector('[data-vx-back-title]');
  const set = (href, title) => {
    link.href = href;
    if (titleNode && title) titleNode.textContent = title;
  };

  // The collection is part of this product URL: the server link is already right; remember it for the next product.
  if (link.dataset.source === 'url') {
    const url = new URL(link.href, location.origin);
    writeCollectionContext(url.pathname, '', titleNode?.textContent?.trim() || '');
    return;
  }

  const referrer = sameStoreReferrer();
  const context = readCollectionContext();

  if (referrer && COLLECTION_PATH.test(referrer.pathname)) {
    // Came straight from a collection page (keeps its filters and sorting).
    const title = context && context.path === referrer.pathname ? context.title : 'collection';
    set(`${referrer.pathname}${referrer.search}`, title);
  } else if (referrer && PRODUCT_PATH.test(referrer.pathname) && context) {
    // Moving between products after browsing a collection in this tab.
    set(`${context.path}${context.search}`, context.title);
  } else if (referrer && SEARCH_PATH.test(referrer.pathname)) {
    set(`${referrer.pathname}${referrer.search}`, 'search results');
  }
  // Otherwise (Google, bookmark, shared link, external site, no history): keep the server link,
  // which is the product's own collection or All products.
}

/* ---------- Product page: keep ViroxaNest blocks in sync with Horizon's variant picker ---------- */

class VxVariantSync extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    if (!this.section) return;
    import('@shopify/events')
      .then(({ StandardEvents }) => {
        this.eventName = StandardEvents.productSelect;
        this.section?.addEventListener(this.eventName, this.onSelect);
      })
      .catch(() => {
        /* Events module unavailable: the block keeps showing the variant the page loaded with. */
      });
  }

  disconnectedCallback() {
    if (this.eventName) this.section?.removeEventListener(this.eventName, this.onSelect);
  }

  onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;
        const fresh = html.querySelector(`vx-variant-sync[data-key="${CSS.escape(this.dataset.key || '')}"]`);
        if (fresh) this.replaceChildren(...Array.from(fresh.childNodes).map((node) => document.importNode(node, true)));
      })
      .catch(() => {});
  };
}

if (!customElements.get('vx-variant-sync')) customElements.define('vx-variant-sync', VxVariantSync);

/* ---------- Product tabs: tabs on desktop, accordions (native <details>) on mobile ---------- */

const tabsDesktop = window.matchMedia('(min-width: 990px)');

/** @param {HTMLElement} root */
function initTabs(root) {
  if (root.dataset.vxReady) return;
  root.dataset.vxReady = 'true';
  const list = root.querySelector('[data-vx-tablist]');
  const panels = Array.from(root.querySelectorAll('[data-vx-tab-panel]'));
  if (!list || !panels.length) return;

  const tabs = panels.map((panel, index) => {
    const id = `${root.closest('.shopify-section')?.id || 'vx'}-tab-${index}`;
    const content = panel.querySelector('.vx-tabs__content');
    if (content) {
      content.id = `${id}-panel`;
      content.setAttribute('role', 'tabpanel');
      content.setAttribute('aria-labelledby', id);
    }
    const tab = el('button', 'vx-tabs__tab', panel.dataset.label || '');
    tab.type = 'button';
    tab.id = id;
    tab.setAttribute('role', 'tab');
    tab.setAttribute('aria-controls', `${id}-panel`);
    list.append(tab);
    return tab;
  });

  const select = (index, focus = false) => {
    panels.forEach((panel, i) => {
      const selected = i === index;
      if (tabsDesktop.matches) panel.open = selected;
      tabs[i].setAttribute('aria-selected', String(selected));
      tabs[i].tabIndex = selected ? 0 : -1;
    });
    if (focus) tabs[index].focus();
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => select(index));
    tab.addEventListener('keydown', (event) => {
      const keys = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
      const move = keys[event.key];
      if (move === undefined) return;
      event.preventDefault();
      let next = move === 'first' ? 0 : move === 'last' ? tabs.length - 1 : index + move;
      next = (next + tabs.length) % tabs.length;
      select(next, true);
    });
  });

  // Keep the tab state and the accordion state aligned when the layout changes.
  panels.forEach((panel, index) => {
    panel.addEventListener('toggle', () => {
      if (panel.open && !tabsDesktop.matches) select(index);
    });
  });

  const sync = () => {
    list.hidden = !tabsDesktop.matches;
    root.classList.toggle('vx-tabs--tabbed', tabsDesktop.matches);
    const openIndex = Math.max(0, panels.findIndex((panel) => panel.open));
    select(openIndex);
  };
  tabsDesktop.addEventListener('change', sync);
  sync();

  // Deep link to #reviews opens that tab.
  if (location.hash === '#reviews') {
    const index = panels.findIndex((panel) => panel.id === 'reviews');
    if (index !== -1) {
      panels[index].open = true;
      select(index);
    }
  }
}

/* ---------- Product description: short preview with Read more / Read less ---------- */

/** @param {HTMLElement} root */
function initReadMore(root) {
  if (root.dataset.vxReady) return;
  const content = root.querySelector('.vx-readmore__content');
  const toggle = root.querySelector('[data-vx-readmore-toggle]');
  if (!(content instanceof HTMLElement) || !(toggle instanceof HTMLButtonElement)) return;

  const setExpanded = (expanded) => {
    root.classList.toggle('is-collapsed', !expanded);
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.textContent = expanded ? 'Read less' : 'Read more';
  };

  // Only collapse when the text is clearly longer than the preview (measured while visible).
  const measure = () => {
    if (root.dataset.vxReady || !content.offsetParent) return;
    root.classList.add('is-collapsed');
    const overflowing = content.scrollHeight > content.clientHeight + 8;
    root.classList.remove('is-collapsed');
    if (!overflowing) return;
    root.dataset.vxReady = 'true';
    toggle.hidden = false;
    setExpanded(false);
  };

  toggle.addEventListener('click', () => {
    const expanded = toggle.getAttribute('aria-expanded') !== 'true';
    setExpanded(expanded);
    if (!expanded) content.closest('.vx-tabs__panel')?.scrollIntoView({ block: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  });

  measure();
  // The description may start inside a closed accordion: measure once it opens.
  root.closest('details')?.addEventListener('toggle', measure);
}

/* ---------- Shared product helpers (read from Shopify's public product JSON) ---------- */

const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function moneyFormatter() {
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR';
  try {
    const formatter = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency });
    return (cents) => formatter.format(cents / 100);
  } catch (error) {
    return (cents) => (cents / 100).toFixed(2);
  }
}

/** @param {string} handle */
function fetchProduct(handle) {
  return fetch(`${shopRoot}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);
}

/**
 * Compact product card built with DOM APIs (text only, never HTML strings).
 * @param {any} product
 * @param {{ format: (cents: number) => string }} options
 */
function buildMiniCard(product, options) {
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const variant = variants.find((v) => v.available) || variants[0];
  const price = Number.isFinite(variant?.price) ? variant.price : product.price;
  const compare = Number.isFinite(variant?.price) ? variant.compare_at_price : product.compare_at_price;

  const item = el('li', 'vx-minicard');
  const media = el('a', 'vx-minicard__media');
  media.href = product.url;
  media.tabIndex = -1;
  media.setAttribute('aria-hidden', 'true');
  const imageSrc = typeof product.featured_image === 'string' ? product.featured_image : '';
  if (/^(https:)?\/\//.test(imageSrc)) {
    const img = el('img', 'vx-minicard__img');
    img.src = `${imageSrc}${imageSrc.includes('?') ? '&' : '?'}width=400`;
    img.alt = '';
    img.width = 400;
    img.height = 400;
    img.loading = 'lazy';
    media.append(img);
  }
  if (compare && compare > price) {
    const pct = Math.floor(((compare - price) * 100) / compare);
    if (pct >= 1) media.append(el('span', 'vx-minicard__badge', `-${pct}%`));
  }
  if (!product.available) media.append(el('span', 'vx-minicard__badge vx-minicard__badge--soldout', 'Sold out'));
  item.append(media);

  const title = el('a', 'vx-minicard__title', product.title);
  title.href = product.url;
  const priceRow = el('p', 'vx-minicard__price');
  priceRow.append(el('span', '', options.format(price)));
  if (compare && compare > price) priceRow.append(el('s', 'vx-minicard__compare', options.format(compare)));
  item.append(title, priceRow);
  return item;
}

/* ---------- Recently viewed (stored only in this browser) ---------- */

const RECENT_KEY = 'vx-recently-viewed';
const RECENT_MAX = 12;

function readRecent() {
  try {
    const value = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string' && HANDLE_PATTERN.test(handle)) : [];
  } catch (error) {
    return [];
  }
}

function writeRecent(list) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch (error) {
    /* storage unavailable: nothing is remembered */
  }
}

/** @param {HTMLElement} root */
async function initRecentlyViewed(root) {
  if (root.dataset.vxReady) return;
  root.dataset.vxReady = 'true';
  const list = root.querySelector('[data-vx-recent-list]');
  if (!list) return;

  const current = root.dataset.current || '';
  const history = readRecent();
  if (current && HANDLE_PATTERN.test(current)) {
    writeRecent([current, ...history.filter((handle) => handle !== current)]);
  }

  const limit = Number(root.dataset.limit) || 8;
  const handles = history.filter((handle) => handle !== current).slice(0, limit);
  if (!handles.length) return;

  const products = (await Promise.all(handles.map(fetchProduct))).filter(Boolean);
  // Forget products that were deleted or unpublished.
  if (products.length !== handles.length) {
    const missing = handles.filter((handle) => !products.some((product) => product.handle === handle));
    writeRecent(readRecent().filter((handle) => !missing.includes(handle)));
  }
  if (!products.length) return;

  const format = moneyFormatter();
  list.replaceChildren(
    ...products.map((product) => buildMiniCard(product, { format }))
  );
  root.hidden = false;
  initScrollRow(root);
}

/** Arrow buttons for a horizontally scrolling row. @param {HTMLElement} root */
function initScrollRow(root) {
  const row = root.querySelector('.vx-cardrow');
  if (!(row instanceof HTMLElement)) return;
  const buttons = Array.from(root.querySelectorAll('[data-vx-scroll]'));
  const update = () => {
    const max = row.scrollWidth - row.clientWidth - 2;
    const start = Math.abs(row.scrollLeft);
    buttons.forEach((button) => {
      const dir = Number(button.getAttribute('data-vx-scroll'));
      button.toggleAttribute('disabled', dir < 0 ? start <= 2 : start >= max);
      button.hidden = max <= 0;
    });
  };
  buttons.forEach((button) =>
    button.addEventListener('click', () => {
      const dir = Number(button.getAttribute('data-vx-scroll')) * (document.dir === 'rtl' ? -1 : 1);
      row.scrollBy({ left: dir * row.clientWidth * 0.8, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    })
  );
  row.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();
}

/* ---------- Header height for Horizon features that offset below the header ---------- */

function trackHeaderHeight(header) {
  if (!('ResizeObserver' in window) || header.dataset.vxObserved) return;
  header.dataset.vxObserved = 'true';
  const section = header.closest('.shopify-section') || header;
  new ResizeObserver(() => {
    document.body.style.setProperty('--header-height', `${section.offsetHeight}px`);
  }).observe(section);
}

/* ---------- Hero carousel ---------- */

/** @param {HTMLElement} root */
function initCarousel(root) {
  if (root.dataset.vxReady) return;
  root.dataset.vxReady = 'true';

  const slides = Array.from(root.querySelectorAll('[data-vx-slide]'));
  const dots = Array.from(root.querySelectorAll('[data-vx-carousel-dot]'));
  const pauseButton = root.querySelector('[data-vx-carousel-pause]');
  if (slides.length < 2) return;

  let index = 0;
  let timer = 0;
  let userPaused = false;
  let hoverPaused = false;
  const delay = Number(root.dataset.autoplay) || 0;

  const goTo = (next) => {
    index = (next + slides.length) % slides.length;
    slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
    });
    dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  };

  const stopTimer = () => window.clearInterval(timer);
  const startTimer = () => {
    stopTimer();
    if (!delay || userPaused || hoverPaused || reducedMotion.matches) return;
    timer = window.setInterval(() => goTo(index + 1), delay);
  };

  root.querySelector('[data-vx-carousel-prev]')?.addEventListener('click', () => goTo(index - 1));
  root.querySelector('[data-vx-carousel-next]')?.addEventListener('click', () => goTo(index + 1));
  dots.forEach((dot) => dot.addEventListener('click', () => goTo(Number(dot.dataset.vxCarouselDot))));

  root.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') goTo(index + (document.dir === 'rtl' ? 1 : -1));
    else if (event.key === 'ArrowRight') goTo(index + (document.dir === 'rtl' ? -1 : 1));
  });

  if (delay) {
    if (reducedMotion.matches) {
      userPaused = true;
      pauseButton?.classList.add('is-paused');
      pauseButton?.setAttribute('aria-label', 'Play slideshow');
    }
    pauseButton?.addEventListener('click', () => {
      userPaused = !userPaused;
      pauseButton.classList.toggle('is-paused', userPaused);
      pauseButton.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
      startTimer();
    });
    const pause = () => {
      hoverPaused = true;
      stopTimer();
    };
    const resume = () => {
      hoverPaused = false;
      startTimer();
    };
    root.addEventListener('mouseenter', pause);
    root.addEventListener('mouseleave', resume);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', (event) => {
      if (!root.contains(/** @type {Node} */ (event.relatedTarget))) resume();
    });
    startTimer();
  }
}

/* ---------- Footer accordions ---------- */

const footerDesktop = window.matchMedia('(min-width: 750px)');

function syncFooterGroups() {
  document.querySelectorAll('[data-vx-footer-group]').forEach((group) => {
    const summary = group.querySelector('summary');
    if (footerDesktop.matches) {
      group.open = true;
      summary?.setAttribute('tabindex', '-1');
    } else {
      summary?.removeAttribute('tabindex');
      if (!group.dataset.vxTouched) group.open = false;
    }
  });
}

document.addEventListener(
  'click',
  (event) => {
    const summary = /** @type {HTMLElement} */ (event.target).closest?.('[data-vx-footer-group] > summary');
    if (!summary) return;
    if (footerDesktop.matches) event.preventDefault();
    else summary.parentElement.dataset.vxTouched = 'true';
  },
  true
);

footerDesktop.addEventListener('change', syncFooterGroups);

/* ---------- Back to top ---------- */

document.addEventListener('click', (event) => {
  const link = /** @type {HTMLElement} */ (event.target).closest?.('[data-vx-back-to-top]');
  if (!link) return;
  event.preventDefault();
  const behavior = reducedMotion.matches ? 'auto' : 'smooth';
  // Horizon scrolls .page-wrapper on desktop and the document on mobile.
  document.querySelector('.page-wrapper')?.scrollTo({ top: 0, behavior });
  window.scrollTo({ top: 0, behavior });
  const target = document.querySelector('.vx-header__logo') || document.getElementById('MainContent');
  target?.focus({ preventScroll: true });
});

/* ---------- Newsletter: focus the result message after Shopify redirects back ---------- */

function focusNewsletterMessage() {
  const message = document.querySelector('.vx-newsletter-form__message:not([hidden])');
  if (!message) return;
  message.closest('details')?.setAttribute('open', '');
  message.focus({ preventScroll: false });
}

/* ---------- Init ---------- */

function init(root = document) {
  root.querySelectorAll('[data-vx-search]').forEach(initVoiceSearch);
  root.querySelectorAll('[data-vx-header]').forEach((header) => {
    initDrawer(header);
    initShortcuts(header);
    trackHeaderHeight(header);
    rememberCollection(header);
  });
  root.querySelectorAll('a[data-vx-back]').forEach((link) => initBackButton(/** @type {HTMLAnchorElement} */ (link)));
  root.querySelectorAll('[data-vx-wishlist-page]').forEach((app) => initWishlistPage(app));
  syncWishlistUI();
  root.querySelectorAll('[data-vx-carousel]').forEach(initCarousel);
  root.querySelectorAll('[data-vx-tabs]').forEach(initTabs);
  root.querySelectorAll('[data-vx-readmore]').forEach((el) => initReadMore(/** @type {HTMLElement} */ (el)));
  root.querySelectorAll('[data-vx-recent]').forEach((section) => initRecentlyViewed(section));
  syncFooterGroups();
}

init();
focusNewsletterMessage();

document.addEventListener('shopify:section:load', (event) => init(/** @type {HTMLElement} */ (event.target)));
document.addEventListener('shopify:block:select', (event) => {
  const slide = /** @type {HTMLElement} */ (event.target).closest?.('[data-vx-slide]');
  const carousel = slide?.closest('[data-vx-carousel]');
  if (!slide || !carousel) return;
  const slides = Array.from(carousel.querySelectorAll('[data-vx-slide]'));
  carousel.querySelectorAll('[data-vx-carousel-dot]')[slides.indexOf(slide)]?.click();
});


/* ---------- Product card image tint: light background picked from each product photo ---------- */
(() => {
const SELECTOR = '.product-card__content .card-gallery img, .vx-hero-card__media img';
const done = new WeakSet();
const canvas = document.createElement('canvas');
canvas.width = canvas.height = 32;
const ctx = canvas.getContext('2d', { willReadFrequently: true });
const tintFor = (img) => {
try {
ctx.clearRect(0, 0, 32, 32);
ctx.drawImage(img, 0, 0, 32, 32);
const d = ctx.getImageData(0, 0, 32, 32).data;
let r = 0, g = 0, b = 0, w = 0, satSum = 0, n = 0;
for (let i = 0; i < d.length; i += 4) {
const R = d[i], G = d[i + 1], B = d[i + 2];
if (d[i + 3] < 200 || (R > 232 && G > 232 && B > 232)) continue;
const s = (Math.max(R, G, B) - Math.min(R, G, B)) / 255;
const wt = s * s + 0.01;
r += R * wt; g += G * wt; b += B * wt; w += wt; satSum += s; n++;
}
if (!n || satSum / n < 0.1) return 'hsl(218 60% 94%)';
const R = r / w / 255, G = g / w / 255, B = b / w / 255;
const mx = Math.max(R, G, B), mn = Math.min(R, G, B), dl = mx - mn;
let h = 218;
if (dl) {
h = mx === R ? ((G - B) / dl) % 6 : mx === G ? (B - R) / dl + 2 : (R - G) / dl + 4;
h = Math.round(h * 60);
if (h < 0) h += 360;
}
const sat = Math.round(Math.min(70, Math.max(40, (satSum / n) * 140)));
return 'hsl(' + h + ' ' + sat + '% 94%)';
} catch (e) {
return null;
}
};
const apply = (img) => {
if (done.has(img)) return;
const box = img.closest('.card-gallery, .vx-hero-card__media');
if (!box) return;
const run = () => {
if (done.has(img) || !img.naturalWidth) return;
done.add(img);
const tint = tintFor(img);
if (tint) box.style.setProperty('--vx-card-tint', tint);
};
if (img.complete && img.naturalWidth) run();
else img.addEventListener('load', run, { once: true });
};
const scan = (root) => root.querySelectorAll && root.querySelectorAll(SELECTOR).forEach(apply);
scan(document);
new MutationObserver((records) => {
records.forEach((record) => record.addedNodes.forEach((node) => {
if (node.nodeType !== 1) return;
if (node.matches && node.matches(SELECTOR)) apply(node);
scan(node);
}));
}).observe(document.documentElement, { childList: true, subtree: true });
})();
/* ---------- Header tools: dark mode toggle (saved per visitor) + language picker ---------- */
(() => {
const KEY = 'vx-theme';
const root = document.documentElement;
const save = (value) => { try { localStorage.setItem(KEY, value); } catch (e) {} };
const sync = () => {
const dark = root.classList.contains('vx-dark');
document.querySelectorAll('[data-vx-theme-toggle]').forEach((button) => {
button.setAttribute('aria-pressed', String(dark));
button.setAttribute('aria-label', dark ? 'Light mode' : 'Dark mode');
});
};
try { if (localStorage.getItem(KEY) === 'dark') root.classList.add('vx-dark'); } catch (e) {}
const nav = document.querySelector('.vx-drawer__nav');
const tools = document.querySelectorAll('.vx-header__actions .vx-lang, .vx-header__actions .vx-theme-toggle');
if (nav && tools.length && !nav.querySelector('.vx-drawer__tools')) {
const box = document.createElement('div');
box.className = 'vx-drawer__tools';
tools.forEach((tool) => box.append(tool.cloneNode(true)));
nav.append(box);
}
document.addEventListener('click', (event) => {
if (!event.target.closest('[data-vx-theme-toggle]')) return;
const dark = !root.classList.contains('vx-dark');
root.classList.toggle('vx-dark', dark);
save(dark ? 'dark' : 'light');
sync();
});
document.addEventListener('change', (event) => {
const select = event.target.closest('.vx-lang__select');
if (select && select.form) select.form.submit();
});
sync();
})();
/* ---------- Sticky category menu: keeps --vx-header-h equal to the sticky header's height ---------- */
(() => {
const header = document.querySelector('[id$="__vx_header"]');
if (!header) return;
const set = () => document.documentElement.style.setProperty('--vx-header-h', `${header.offsetHeight}px`);
set();
if ('ResizeObserver' in window) new ResizeObserver(set).observe(header);
else window.addEventListener('resize', set);
})();
